import React, { useEffect, useState } from "react";
import styles from "./Footer.module.css";
import { getFooterInfo } from "../../lib/getFooterInfo";

type ExtraLink = {
  id: number;
  name: string;
  title: string;
  url: string;
  icon?: string;
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

const Footer: React.FC = () => {
  const [footerContent, setFooterContent] = useState<any[]>([]);

  useEffect(() => {
    const fetchFooterContent = async () => {
      try {
        const cachedFooter = localStorage.getItem("footerContent");
        const cachedTimestamp = localStorage.getItem("footerContentTimestamp");

        if (cachedFooter && cachedTimestamp) {
          const isCacheValid =
            Date.now() - parseInt(cachedTimestamp, 10) < CACHE_DURATION;
          if (isCacheValid) {
            setFooterContent(JSON.parse(cachedFooter));
            return;
          }
        }

        const data = await getFooterInfo();
        const footerData = data?.data?.Footer || [];
        setFooterContent(footerData);
        localStorage.setItem("footerContent", JSON.stringify(footerData));
        localStorage.setItem("footerContentTimestamp", Date.now().toString());
      } catch (error) {
        console.error("Error fetching footer content:", error);
      }
    };

    fetchFooterContent();
  }, []);

  // Additional links as per your configuration
  const additionalLinks = {
    "Sobre Evefant": [
      { id: 101, name: "Blog", title: "Visita nuestro Blog", url: "blog" },
    ],
    "Categorias Populares": [
      {
        id: 103,
        name: "Globos",
        title: "Explora nuestra selección de Globos",
        url: "globos",
      },
    ],
    Ayuda: [
      {
        id: 105,
        name: "Libro de Reclamaciones",
        url: "formulario/libro-de-reclamaciones",
        icon: "libro_de_reclamaciones_6ff9a3ca29.svg",
        title: "Libro de Reclamaciones",
      },
    ],
  };

  const paymentMethods = [
    {
      title: "Tarjetas de Crédito / Débito",
      logos: [
        "payment_method_visa_c9155cb7f2.svg",

        "payment_method_mastercard_0899ee072b.svg",

        "payment_method_amex_90df66770a.svg",

        "payment_method_diners_8a21d180a8.svg",
      ],
    },
    {
      title: "Billeteras Digitales",
      logos: [
        "payment_method_yape_1018da3083.svg",

        "payment_method_plin_5726e9e991.svg",
      ],
    },
    {
      title: "Transferencias Bancarias",
      logos: ["payment_method_transfer_00308a1a73.svg"],
    },
  ];

  const socialMediaLinks = [
    {
      url: "https://facebook.com/evefantpe",
      name: "Facebook",
      icon: "logo_fb_evf_3019665e8c.webp",
      title: "Visitar Facebook de Evefant",
    },

    {
      url: "https://instagram.com/evefant.pe",
      name: "Instagram",
      icon: "logo_ig_evf_495dc27630.webp",
      title: "Visitar Instagram de Evefant",
    },
    {
      url: "https://www.tiktok.com/@evefant.pe",
      name: "TikTok",
      icon: "logo_tt_evf_97a899fc74.webp",
      title: "Visitar TikTok de Evefant",
    },
    {
      url: "http://linkedin.com/company/evefantpe",
      name: "LinkedIn",
      icon: "logo_link_evf_c5a19490d0.webp",
      title: "Visitar LinkedIn de Evefant",
    },

    {
      url: "https://www.pinterest.com/evefantpe/",
      name: "Pinterest",
      icon: "logo_pin_evf_f1ce5b18a9.webp",
      title: "Visitar Pinterest de Evefant",
    },
  ];

  return (
    <div className={styles.footer}>
      <div className={styles.footerContainer}>
        {/* Dynamically loaded footer sections */}
        {footerContent.map((section: any) => (
          <div className={styles.footerSection} key={section.id}>
            <h3 className={styles.sectionTitle}>{section.sectionName}</h3>
            <ul className={styles.linksList}>
              {section.sectionItem.map((link: any) => (
                <li key={link.id} className={styles.linkItem}>
                  <a
                    href={`/${link.url}`}
                    className={styles.link}
                    title={`Ir a ${link.name}`} // Add title attribute
                  >
                    {link.name}
                  </a>
                </li>
              ))}
              {additionalLinks[
                section.sectionName as keyof typeof additionalLinks
              ]?.map((extraLink: ExtraLink) => (
                <li key={extraLink.id} className={styles.linkItem}>
                  <a
                    href={`/${extraLink.url}`}
                    className={styles.link}
                    title={extraLink.title || `Visitar ${extraLink.name}`}
                  >
                    {extraLink.icon && (
                      <img
                        src={`${process.env.REACT_APP_IMAGE_FOLDER}/${extraLink.icon}`}
                        alt={`${extraLink.name} icon`}
                        className={styles.icon}
                        title={`Ícono de ${extraLink.name}`} // Add title to icon
                        width="60"
                        height="60"
                      />
                    )}
                    {extraLink.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Síguenos En Section */}
        <div className={styles.footerSection}>
          <h4 className={styles.sectionTitle}>Síguenos En</h4>
          <div className={styles.socialMediaIcons}>
            {socialMediaLinks.map((social, index) => (
              <a
                href={social.url}
                key={index}
                className={styles.socialMediaLink}
                target="_blank"
                rel="noopener noreferrer"
                title={
                  social.title ||
                  `Visítanos en ${getSocialNetworkName(social.url)}`
                }
              >
                <img
                  src={`${process.env.REACT_APP_IMAGE_FOLDER}/${social.icon}`}
                  alt={`${social.name} icon`}
                  className={styles.icon}
                  title={`Ícono de ${social.name}`}
                  width="60"
                  height="60"
                />
              </a>
            ))}
          </div>
        </div>

        {/* Métodos de Pago Section */}
        <div className={styles.footerSection}>
          <h4 className={styles.sectionTitle}>Métodos de Pago</h4>
          {paymentMethods.map((method, index) => (
            <div key={index} className={styles.paymentMethod}>
              <h5 className={styles.paymentTitle}>{method.title}</h5>
              <div className={styles.paymentLogos}>
                {method.logos.map((logo, idx) => (
                  <img
                    key={idx}
                    src={`${process.env.REACT_APP_IMAGE_FOLDER}/${logo}`}
                    alt={`${method.title} logo ${getPaymentMethodName(logo)}`}
                    className={styles.icon}
                    title={`Método de pago: ${getPaymentMethodName(logo)}`} // Add title attribute
                    width="60"
                    height="60"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Helper function to extract social network name from URL
function getSocialNetworkName(url: string): string {
  if (url.includes("facebook")) return "Facebook";
  if (url.includes("instagram")) return "Instagram";
  if (url.includes("tiktok")) return "TikTok";
  if (url.includes("linkedin")) return "LinkedIn";
  if (url.includes("pinterest")) return "Pinterest";
  return "Social Media";
}

// Helper function to get payment method name from logo filename
function getPaymentMethodName(logoFilename: string): string {
  if (logoFilename.includes("visa")) return "Visa";
  if (logoFilename.includes("mastercard")) return "Mastercard";
  if (logoFilename.includes("amex")) return "American Express";
  if (logoFilename.includes("diners")) return "Diners Club";
  if (logoFilename.includes("yape")) return "Yape";
  if (logoFilename.includes("plin")) return "Plin";
  if (logoFilename.includes("transfer")) return "Transferencia Bancaria";
  return "Método de pago";
}

export default React.memo(Footer);
